import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { Label } from 'components';
import { Multiselect, M, Flex } from 'design-system-web';
import { getAvailableCompanyRoles, assignCompanyRole } from 'graphql-queries';
import { useQuery, useMutation } from '@apollo/client';
import type { AssignCompanyRoleResult } from 'graphql-query-contracts';

type CompanyRoleOption = {
  id: string;
  name: string;
  templateId: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  bulkSelectedUsers: {
    id: string;
    name: string;
    email: string;
    company: { _id: string; name: string }[];
  }[];
  onSuccess?: () => void;
  propertyId: string;
};

const TRANSLATION_KEYS = {
  modalTitle:
    'web.admin.channel.users.all.table.bulkAssignCompanyRole.modal.title',
  confirmButtonLabel:
    'web.admin.channel.users.all.table.bulkAssignCompanyRole.modal.confirmButton.label',
  cancelButtonLabel:
    'web.admin.channel.users.all.table.bulkAssignCompanyRole.modal.cancelButton.label',
  promptText:
    'web.admin.channel.users.all.table.bulkAssignCompanyRole.modal.prompt.text',
  roleDropdownLabel:
    'web.admin.channel.users.all.table.bulkAssignCompanyRole.modal.roleDropdown.label',
  toastText:
    'web.admin.channel.users.all.table.bulkAssignCompanyRole.success.toast.text',
  errorMessage:
    'web.admin.channel.users.all.table.bulkAssignCompanyRole.failed.alert.text',
};

export const BulkAssignCompanyRoleModal = ({
  isOpen,
  onClose,
  bulkSelectedUsers,
  onSuccess,
  propertyId,
}: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<{ value: string }[]>([]);

  const { data: companyRolesData } = useQuery(getAvailableCompanyRoles, {
    variables: {
      propertyId,
    },
    skip: !isOpen,
    fetchPolicy: 'cache-and-network',
  });

  const [assignCompanyRoleMutation] = useMutation(assignCompanyRole);

  const availableRoles: CompanyRoleOption[] =
    companyRolesData?.getAvailableCompanyRoles?.map(
      (role: CompanyRoleOption) => ({
        id: role.id,
        name: role.name,
        templateId: role.templateId,
      })
    ) ?? [];

  const roleOptions = availableRoles.map((role: CompanyRoleOption) => ({
    value: role.templateId,
    label: role.name,
  }));

  const handleRolesChange = (roles: { value: string }[]) => {
    setSelectedRoles(roles);
  };

  const handleConfirm = async () => {
    if (selectedRoles.length === 0) {
      return;
    }

    try {
      setLoading(true);

      const selectedRoleIds = selectedRoles.map(({ value }) => value);

      const userIds = bulkSelectedUsers.map(user => user.id);

      const { data } = await assignCompanyRoleMutation({
        variables: {
          userIds,
          roleTemplateIds: selectedRoleIds,
          channelId: propertyId,
        },
      });

      const results: AssignCompanyRoleResult[] = data?.assignCompanyRole ?? [];
      const successCount = results.filter(
        (result: AssignCompanyRoleResult) => result.success
      ).length;
      const skippedCount = results.length - successCount;

      // Show success message
      window.Toast.show(t(TRANSLATION_KEYS.toastText), 5000);

      // Show warning if some users were skipped
      if (skippedCount > 0) {
        window.Toast.show(
          t(
            'web.admin.channel.users.all.table.bulkAssignCompanyRole.modal.skippedUsers.message'
          ),
          5000
        );
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      await window.Alert.alert({
        title: t(TRANSLATION_KEYS.errorMessage),
        error: err,
      });
    }

    setLoading(false);
    setSelectedRoles([]);
    onClose();
  };

  const handleClose = async () => {
    setSelectedRoles([]);
    onClose();
  };

  const bulkSelectedUsersCount = bulkSelectedUsers.length;
  const selectedRolesCount = selectedRoles.length;

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={handleClose}
      handleConfirm={handleConfirm}
      title={t(TRANSLATION_KEYS.modalTitle)}
      confirmText={t(TRANSLATION_KEYS.confirmButtonLabel, {
        count: selectedRolesCount,
      })}
      cancelText={t(TRANSLATION_KEYS.cancelButtonLabel)}
      loading={loading}
      confirmActionDisabled={selectedRolesCount === 0 || loading}
      cancelActionDisabled={loading}
      isFullWidth
    >

      <M>
        <Trans
          components={[<b />]}
          i18nKey={TRANSLATION_KEYS.promptText}
          count={bulkSelectedUsersCount}
          values={{
            count: bulkSelectedUsersCount,
          }}
        />
      </M>

      <Flex direction="column" gap={1} m={[4, 0, 0, 0]}>
        <Label>
          {t(TRANSLATION_KEYS.roleDropdownLabel)}
        </Label>
        <Multiselect
          id="role-select"
          testId="company-role-dropdown"
          items={roleOptions}
          value={selectedRoles}
          onChange={handleRolesChange}
          placeholder={t(
            'web.admin.channel.users.all.table.bulkAssignCompanyRole.modal.roleDropdown.placeholder'
          )}
          isFullWidth
          doTranslation={false}
        />
      </Flex>
    </ConfirmationModal>
  );
};

